import cn from 'classnames';
import React from 'react';
import Image from 'next/image';

import type { ColourSwatchModel } from './useColourSwatchModel';
import styles from './ColourSwatch.module.scss';

export interface ColourSwatchViewProps extends ColourSwatchModel {
  wrapperClassName: string;
  buttonClassName: string;
  imageWidth: number;
  imageHeight: number;
  /** `next/image` `sizes`; defaults to the swatch box size. */
  imageSizes?: string;
  tabIndex?: number;
}

/**
 * Presentational shell: layout + image/hex + deal dot. Variant-specific classes
 * and dimensions are supplied by ColourSwatchPdp / ColourSwatchCard.
 */
const ColourSwatchView: React.FC<ColourSwatchViewProps> = ({
  colour,
  displayColourName,
  hasImage,
  hasHex,
  isNoData,
  disabledLabel,
  onSelect,
  onPreload,
  wrapperClassName,
  buttonClassName,
  imageWidth,
  imageHeight,
  imageSizes,
  tabIndex,
}) => (
  <div className={wrapperClassName}>
    <button
      type="button"
      className={buttonClassName}
      role="radio"
      aria-checked={colour.isSelected}
      aria-label={displayColourName}
      title={displayColourName}
      tabIndex={tabIndex}
      onClick={onSelect}
      onMouseEnter={onPreload}
    >
      <span className={styles.screenReader}>
        {displayColourName}
        {disabledLabel}
      </span>
      {hasImage && colour.swatchImage ? (
        <span className={styles.inner}>
          {/* Fills the square swatch and crops with `object-fit: cover`, so
              non-square swatch art (e.g. the landscape gift card designs)
              isn't pinned to the top of the box. */}
          <Image
            fill
            sizes={imageSizes ?? `${Math.max(imageWidth, imageHeight)}px`}
            src={colour.swatchImage}
            className={styles.imageInner}
            alt=""
            quality={60}
          />
        </span>
      ) : (
        <span
          className={styles.inner}
          style={{
            backgroundColor: isNoData
              ? 'var(--color-white)'
              : hasHex
              ? colour.hexColours?.[0]
              : undefined,
          }}
        />
      )}
    </button>
    <span
      className={cn(styles.dealDot, {
        [styles.dealDotActive]: colour.isDeal,
      })}
      aria-hidden="true"
    />
  </div>
);

export default ColourSwatchView;
